import React, { useEffect, useMemo, useRef, useState } from "react"
import { Button, Input, Spin, message } from "antd"
import { Link, useHistory, useParams } from "react-router-dom"
import {
    CreditPullPaymentError,
    getCreditPullPaymentRequestV2,
    payCreditPullPaymentRequestV2,
} from "./api/creditPullPaymentV2Client"
import { getApplicationV2 } from "./api/borrowerSideV2Client"
import {
    listCreditPullProfileGapsForPartyIds,
    partyIdsFromPaymentLineItems,
    showCreditPullProfileGapWarning,
} from "./creditPullProfileGaps"
import TopNavBar from "../newContent/page/TopNavbar"
import "../newContent/page/styles/loans.css"
import "./CreditReportPayment.css"
import { track } from "../statsig"
import { PAGE, STATSIG } from "../statsig/events"

const onlyDigits = (value = "") => `${value}`.replace(/\D/g, "")

const splitBorrowerName = (fullName = "") => {
    const parts = `${fullName}`.trim().split(/\s+/).filter(Boolean)
    if (!parts.length) return { firstName: "", lastName: "" }
    if (parts.length === 1) return { firstName: parts[0], lastName: "" }
    return { firstName: parts[0], lastName: parts.slice(1).join(" ") }
}

const formatCardNumber = (value) => {
    const digits = onlyDigits(value).slice(0, 19)
    return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim()
}

const formatExpiry = (value) => {
    const digits = onlyDigits(value).slice(0, 4)
    if (digits.length <= 2) return digits
    return `${digits.slice(0, 2)} / ${digits.slice(2)}`
}

const formatMoney = (amount) => {
    const n = Number(amount)
    if (!Number.isFinite(n)) return "$0.00"
    return `$${n.toFixed(2)}`
}

/** English list: "A", "A and B", "A, B, and C". */
const formatApplicantNames = (names, fallback = "Primary borrower") => {
    const list = (Array.isArray(names) ? names : [])
        .map((n) => `${n || ""}`.trim())
        .filter(Boolean)
    if (!list.length) return fallback
    if (list.length === 1) return list[0]
    if (list.length === 2) return `${list[0]} and ${list[1]}`
    return `${list.slice(0, -1).join(", ")}, and ${list[list.length - 1]}`
}

/** Parse names from summary labels like "Joint · A & B" / "Individual · A". */
const applicantNamesFromSummaries = (summaries) => {
    const names = []
    const seen = new Set()
    ;(summaries || []).forEach((item) => {
        const label = `${item?.label || ""}`.trim()
        const sep = label.indexOf(" · ")
        if (sep < 0) return
        label.slice(sep + 3).split(/\s*&\s*/).forEach((part) => {
            const name = part.trim()
            if (!name || seen.has(name)) return
            seen.add(name)
            names.push(name)
        })
    })
    return names
}

/** Card-statement style label from company name, e.g. Summit Mortgage → SUMMIT MTG *CREDIT RPT */
const formatStatementDescriptor = (companyName) => {
    const raw = `${companyName || ""}`.trim().toUpperCase()
    if (!raw) return "YOUR LENDER *CREDIT RPT"
    const compact = raw.replace(/\bMORTGAGES?\b/g, "MTG").replace(/\s+/g, " ").trim()
    return `${compact} *CREDIT RPT`
}

/** Convert MM / YY display value to YYYY-MM for SmartAPI. */
const toExpirationYYYYMM = (expiryDisplay) => {
    const digits = onlyDigits(expiryDisplay)
    if (digits.length < 4) return ""
    const mm = Number(digits.slice(0, 2))
    const yy = digits.slice(2, 4)
    if (!Number.isFinite(mm) || mm < 1 || mm > 12) return ""
    return `20${yy}-${String(mm).padStart(2, "0")}`
}

/** Borrower-facing copy — never dump MISMO / vendor XML. */
const friendlyBorrowerError = (raw, { critical = false } = {}) => {
    const text = `${raw || ""}`.trim()
    if (!text) return "Payment failed. Please check your card details and try again."
    if (critical || /do not (pay|retry)/i.test(text)) {
        return "Something went wrong after payment may have started. Please do not pay again — contact your loan officer."
    }
    // Surface SmartAPI / CRA card outcomes (e.g. expired) — do not rewrite or length-truncate.
    if (/\b(this\s+)?credit\s+card\s+expired\b|\bcard\s+expired\b/i.test(text)
        || /request response failed:/i.test(text)) {
        return text
    }
    // Card / expiry *format* problems only — not past-dated cards (those come from SmartAPI).
    if (/ServicePaymentCreditAccountExpirationDate|expiration date|expir.*invalid|MISMODate|invalid according to its datatype/i.test(text)
        || /\b(19|20)\d{2}-(?:0[3-9]|1[3-9]|[2-9]\d)\b/.test(text)) {
        return "Card expiration date looks invalid. Use MM / YY and try again."
    }
    if (/\b(declin|avs|cvc|cvv|zip code|postal code)\b/i.test(text)
        || /secondary.?account|account.?identifier.*(invalid|declin|fail)/i.test(text)) {
        return "Card details were declined or could not be verified. Please check the number, expiry, CVC, and ZIP, then try again."
    }
    if (/xml validation|mismo|ServicePayment|xmlns|schemas:|https?:\/\//i.test(text) || text.length > 160) {
        return "We could not complete this credit-report payment. Please try again, or contact your loan officer if it keeps failing."
    }
    return text
}

/** Login.js reads URLSearchParams "customerid" (not "customer"). */
const getQueryCustomerId = () => {
    try {
        return (new URLSearchParams(window.location.search).get("customerid") || "").trim()
    } catch (_) {
        return ""
    }
}

const appendCustomerIdToPath = (pathOrUrl, customerId) => {
    if (!customerId) return pathOrUrl
    try {
        const isAbs = /^https?:\/\//i.test(pathOrUrl)
        const u = isAbs ? new URL(pathOrUrl) : new URL(pathOrUrl, window.location.origin)
        if (!u.searchParams.get("customerid")) {
            u.searchParams.set("customerid", customerId)
        }
        return isAbs ? u.toString() : `${u.pathname}${u.search}${u.hash}`
    } catch (_) {
        if (/[?&]customerid=/.test(pathOrUrl)) return pathOrUrl
        const sep = pathOrUrl.indexOf("?") >= 0 ? "&" : "?"
        return `${pathOrUrl}${sep}customerid=${encodeURIComponent(customerId)}`
    }
}

const paymentReturnPath = (requestId) => {
    const cid = getQueryCustomerId()
    let path = `/application/credit-report-payment/${requestId}`
    if (cid) path += `?customerid=${encodeURIComponent(cid)}`
    return path
}

const redirectToLogin = (returnPath) => {
    sessionStorage.setItem("zeitroReturnTo", returnPath)
    const pathsRaw = sessionStorage.getItem("paths")
    let loginPath = "/services/signin"
    try {
        if (pathsRaw) {
            const paths = JSON.parse(pathsRaw)
            if (paths.login) loginPath = paths.login
        }
    } catch (_) { /* ignore */ }
    window.location.href = appendCustomerIdToPath(loginPath, getQueryCustomerId())
}

const CreditReportPayment = () => {
    const { requestId } = useParams()
    const history = useHistory()
    const [loading, setLoading] = useState(true)
    const [paying, setPaying] = useState(false)
    const [request, setRequest] = useState(null)
    const [payResult, setPayResult] = useState(null)
    const [blockRetry, setBlockRetry] = useState(false)
    const [cardNumber, setCardNumber] = useState("")
    const [expiry, setExpiry] = useState("")
    const [cvc, setCvc] = useState("")
    const [zip, setZip] = useState("")
    const payingLockRef = useRef(false)

    useEffect(() => {
        track(STATSIG.COMMON.VIEW, PAGE.CREDIT_REPORT_PAYMENT, {
            page: PAGE.CREDIT_REPORT_PAYMENT,
        })
    }, [])

    useEffect(() => {
        const token = sessionStorage.getItem("ZeitroA")
        if (!token) {
            redirectToLogin(paymentReturnPath(requestId))
            return
        }
        let cancelled = false
        ;(async () => {
            setLoading(true)
            try {
                const body = await getCreditPullPaymentRequestV2(requestId)
                if (!cancelled) setRequest(body.Request || null)
            } catch (err) {
                if (!cancelled) {
                    message.error(err.message || "Unable to load payment request")
                    setRequest(null)
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()
        return () => { cancelled = true }
    }, [requestId])

    const amountLabel = useMemo(() => formatMoney(request?.amount), [request])
    const { firstName: borrowerFirstName, lastName: borrowerLastName } = useMemo(
        () => splitBorrowerName(request?.borrowerName),
        [request?.borrowerName],
    )

    const wrapWithHeader = (body) => (
        <div className="crp-shell">
            <TopNavBar firstName={borrowerFirstName} lastName={borrowerLastName} />
            {body}
        </div>
    )

    const canPay = request?.status === "pending"
        && onlyDigits(cardNumber).length >= 13
        && onlyDigits(expiry).length >= 4
        && onlyDigits(cvc).length >= 3
        && zip.trim().length >= 5
        && !paying
        && !blockRetry

    const onPay = async () => {
        if (!canPay || payingLockRef.current) return
        const expirationDate = toExpirationYYYYMM(expiry)
        if (!expirationDate) {
            message.error("Enter a valid expiry as MM / YY.")
            return
        }
        const partyIds = partyIdsFromPaymentLineItems(request?.lineItems)
        if (partyIds.length) {
            try {
                const body = await getApplicationV2()
                const profileGaps = listCreditPullProfileGapsForPartyIds(body.Application, partyIds)
                if (profileGaps.length) {
                    showCreditPullProfileGapWarning(
                        profileGaps,
                        "Fill in SSN and Current residential address in your application before paying.",
                    )
                    return
                }
            } catch (err) {
                message.error(err.message || "Unable to verify borrower information. Please try again.")
                return
            }
        }
        payingLockRef.current = true
        setPaying(true)
        setPayResult(null)
        try {
            // Do not send amount — CRA prices the pull (AdvancedSample / Path-A).
            const body = await payCreditPullPaymentRequestV2(requestId, {
                servicePayment: {
                    accountIdentifier: onlyDigits(cardNumber),
                    expirationDate,
                    secondaryAccountIdentifier: onlyDigits(cvc),
                    postalCode: zip.trim(),
                },
            })
            setPayResult({
                kind: "success",
                succeeded: body.succeeded || [],
                message: body.Message || "Payment successful.",
            })
            track(STATSIG.PAYMENT.PAY, "credit_pull", {
                type: "credit_pull",
                result: "success",
                request_id: requestId,
                loan_id: request?.loanId || "",
                amount: String(request?.amount ?? ""),
            })
            message.success(body.Message || "Payment successful. Your credit report is being processed.")
            setCardNumber("")
            setExpiry("")
            setCvc("")
            history.push("/application/myloans")
        } catch (err) {
            const payload = err instanceof CreditPullPaymentError ? (err.payload || {}) : {}
            const succeeded = payload.succeeded || []
            const failedRaw = payload.failed
            const failed = Array.isArray(failedRaw)
                ? failedRaw
                : (failedRaw ? [failedRaw] : [])
            const remainingAmount = payload.remainingAmount
            const msg = err.message || "Payment failed. Please try again."
            const critical = /do not (pay|retry)/i.test(msg)
            track(STATSIG.PAYMENT.PAY, "credit_pull", {
                type: "credit_pull",
                result: "fail",
                request_id: requestId,
                loan_id: request?.loanId || "",
                amount: String(request?.amount ?? ""),
            })
            if (critical) setBlockRetry(true)
            setPayResult({
                kind: "error",
                message: friendlyBorrowerError(msg, { critical }),
                partial: !!payload.partial || succeeded.length > 0,
                critical,
                succeeded,
                failed: failed.map((item) => ({
                    ...item,
                    error: friendlyBorrowerError(item?.error || msg, { critical }),
                })),
                remainingAmount,
            })
            if (critical) {
                message.error(friendlyBorrowerError(msg, { critical: true }))
            } else if (succeeded.length > 0 || payload.partial) {
                message.warning(friendlyBorrowerError(msg))
            } else {
                message.error(friendlyBorrowerError(msg))
            }
            try {
                const body = await getCreditPullPaymentRequestV2(requestId)
                setRequest(body.Request || null)
            } catch (_) { /* keep existing request */ }
        } finally {
            setPaying(false)
            payingLockRef.current = false
        }
    }

    if (loading) {
        return wrapWithHeader(
            <div className="crp-page">
                <Spin />
            </div>,
        )
    }

    if (!request) {
        return wrapWithHeader(
            <div className="crp-page">
                <Link className="crp-back" to="/application/myloans">← Back to my loans</Link>
                <div className="crp-empty">This payment link is invalid or no longer available.</div>
            </div>,
        )
    }

    if (request.status !== "pending") {
        const cancelled = request.status === "cancelled"
        return wrapWithHeader(
            <div className="crp-page">
                <Link className="crp-back" to="/application/myloans">← Back to my loans</Link>
                <h1 className="crp-title">{cancelled ? "This payment link is no longer valid" : "Payment already completed"}</h1>
                <p className="crp-intro">
                    {cancelled
                        ? "Your loan officer sent a newer payment link. Please use the latest email, or check My loans for a current payment request."
                        : "This credit report payment is no longer pending."}
                </p>
            </div>,
        )
    }

    const loName = request.loanOfficerName || "Your loan officer"
    const remainingSummaries = request.lineItemSummaries || []
    const fromApiApplicantNames = request.applicantNames || []
    const fromSummaryApplicantNames = applicantNamesFromSummaries(remainingSummaries)
    const applicantNamesForDisplay = fromSummaryApplicantNames.length > fromApiApplicantNames.length
        ? fromSummaryApplicantNames
        : (fromApiApplicantNames.length ? fromApiApplicantNames : fromSummaryApplicantNames)
    const priorSucceededCount = request.completedReportCount || 0
    const showPriorPartial = priorSucceededCount > 0
    const showPriorRetryHint = !showPriorPartial && !!request.lastError && payResult?.kind !== "error"
    const payResultSucceeded = payResult?.succeeded || []
    const payResultFailed = payResult?.failed || []
    const payResultTone = !payResult || payResult.kind !== "error"
        ? null
        : (payResult.critical
            || (payResult.partial && payResultSucceeded.length === 0)
            || (!payResult.partial)
            ? "error"
            : "warn")

    return wrapWithHeader(
        <div className="crp-page">
            <Link className="crp-back" to="/application/myloans">← Back to my loans</Link>
            <h1 className="crp-title">Pay for your credit report</h1>
            <p className="crp-intro">
                {loName}, your loan officer, needs to run a credit report for your loan application.
                The report runs automatically once you pay.
            </p>

            <div className="crp-card">
            <div className="crp-summary">
                <div className="crp-summary-row">
                    <span>{request.reportDescription || "Hard credit report · tri-merge"}</span>
                    <strong>{amountLabel}</strong>
                </div>
                <div className="crp-summary-meta">Bureaus included: Experian, TransUnion, Equifax</div>
                <div className="crp-summary-meta">
                    Applicants: {formatApplicantNames(
                        applicantNamesForDisplay,
                        request.borrowerName || "Primary borrower",
                    )}
                </div>
                {remainingSummaries.length > 1 && (
                    <ul className="crp-summary-ops">
                        {remainingSummaries.map((item, idx) => (
                            <li key={`sum-${idx}`}>{item.label} — {formatMoney(item.amount)}</li>
                        ))}
                    </ul>
                )}
                <div className="crp-summary-note">
                    This amount is set by your lender as its cost for the report — no markup or card surcharge is added.
                </div>
            </div>

            <div className="crp-form">
                <label className="crp-label">Card number</label>
                <Input
                    value={cardNumber}
                    onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                    placeholder="ACCT-000006"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    disabled={paying}
                />
                <div className="crp-row">
                    <div>
                        <label className="crp-label">Expiry (MM / YY)</label>
                        <Input
                            value={expiry}
                            onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                            placeholder="MM / YY"
                            inputMode="numeric"
                            autoComplete="cc-exp"
                            disabled={paying}
                        />
                    </div>
                    <div>
                        <label className="crp-label">CVC</label>
                        <Input
                            value={cvc}
                            onChange={(e) => setCvc(onlyDigits(e.target.value).slice(0, 4))}
                            placeholder="123"
                            inputMode="numeric"
                            autoComplete="cc-csc"
                            disabled={paying}
                        />
                    </div>
                    <div>
                        <label className="crp-label">Billing ZIP</label>
                        <Input
                            value={zip}
                            onChange={(e) => setZip(e.target.value.slice(0, 10))}
                            placeholder="94105"
                            autoComplete="postal-code"
                            disabled={paying}
                        />
                    </div>
                </div>
            </div>

            <div className="crp-alerts">
                {showPriorPartial && (
                    <div className="crp-result crp-result-warn">
                        <div className="crp-result-title">Previous attempt partially completed</div>
                        <div className="crp-result-line">
                            Already succeeded: {priorSucceededCount} credit pull
                            {priorSucceededCount === 1 ? "" : "s"} (not charged again).
                        </div>
                        <div className="crp-result-line crp-result-remaining">
                            Remaining due now: <strong>{amountLabel}</strong>
                        </div>
                        {remainingSummaries.length > 0 && (
                            <ul className="crp-result-list">
                                {remainingSummaries.map((item, idx) => (
                                    <li key={`${item.label}-${idx}`}>
                                        {item.label} — {formatMoney(item.amount)}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {showPriorRetryHint && (
                    <div className="crp-result crp-result-warn">
                        <div className="crp-result-title">Previous payment did not complete</div>
                        <div className="crp-result-line">
                            {friendlyBorrowerError(request.lastError)}
                        </div>
                        <div className="crp-result-line crp-result-remaining">
                            Amount due: <strong>{amountLabel}</strong>
                        </div>
                    </div>
                )}

                {payResult?.kind === "error" && payResultTone && (
                    <div className={`crp-result crp-result-${payResultTone}`}>
                        <div className="crp-result-title">
                            {payResult.critical
                                ? "Do not pay again"
                                : (payResult.partial && payResultSucceeded.length > 0
                                    ? "Partial success — remaining payment still needed"
                                    : "Payment failed")}
                        </div>
                        <div className="crp-result-line">{payResult.message}</div>
                        {payResultSucceeded.length > 0 && (
                            <ul className="crp-result-list">
                                {payResultSucceeded.map((item, idx) => (
                                    <li key={`ok-${idx}`}>Succeeded — {item.label}</li>
                                ))}
                            </ul>
                        )}
                        {payResultFailed.length > 0 && (
                            <ul className="crp-result-list">
                                {payResultFailed.map((item, idx) => (
                                    <li key={`fail-${idx}`}>
                                        Failed — {item.label}
                                        {item.error ? `: ${item.error}` : ""}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {typeof payResult.remainingAmount === "number" && (
                            <div className="crp-result-line crp-result-remaining">
                                Remaining to pay: <strong>{formatMoney(payResult.remainingAmount)}</strong>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <Button
                type="primary"
                className="crp-pay-btn"
                disabled={!canPay}
                loading={paying}
                onClick={onPay}
            >
                Pay {amountLabel}
            </Button>
            <div className="crp-footer-wait">
                Payment opens once both applicants have authorized.
            </div>
            </div>

            <div className="crp-footer-notes">
                <div className="crp-footer-secure">
                    <span className="crp-footer-lock" aria-hidden>🔒</span>
                    <span>
                        256-bit encrypted · ZIP is used to verify your card (AVS) · Card details are never stored
                    </span>
                </div>
                <div>
                    Your statement will show {formatStatementDescriptor(request.companyName)}
                </div>
                <div>
                    This fee covers your credit report only. If the report cannot be completed, contact your loan officer about a refund.
                </div>
            </div>
        </div>,
    )
}

export default CreditReportPayment
